// Flat-network diagram (artboard C2): inbound POs fan out directly to peer
// DCs — one hop, no center node — then green bands converge on Sales. Same
// encoding as the hub-and-spoke map: blue edge width ∝ units inbound, green
// band width ∝ weekly outflow, left bar / border = WOS-vs-target health.
//
// Same two rendering rules as network_map_hub_spoke.tsx: edges first as one
// layer, then all labels in the haloed group.
import {
  SnapshotChannel,
  SnapshotInbound,
  SnapshotLocation,
  SnapshotPeriod,
  SnapshotSales,
  SUPPLY_ABBREV,
  SUPPLY_UNIT_LABEL,
} from "./types";
import { SalesNode, salesNodeHeight } from "./sales_node";
import { EM_DASH, un, usd, useSnapshotTokens } from "./primitives";
import { SnapshotTokens } from "./primitives";
import { wosHealth } from "./types";

const healthColor = (
  t: SnapshotTokens,
  health: ReturnType<typeof wosHealth>
): string => {
  if (health === "under") {
    return t.redInk;
  }
  if (health === "light" || health === "heavy") {
    return t.orangeInk;
  }
  if (health === "on") {
    return t.greenInk;
  }
  return t.ink5;
};

const unOr = (v: number | null): string => (v == null ? EM_DASH : un(v));
const usdOr = (v: number | null, digits = 1): string =>
  v == null ? EM_DASH : usd(v, digits);
const clip = (s: string, max: number): string =>
  s.length > max ? s.slice(0, max - 1).trimEnd() + "…" : s;

export const NetworkMapFlat = ({
  locations,
  inbound,
  sales,
  channels,
  period,
}: {
  locations: SnapshotLocation[];
  inbound: SnapshotInbound;
  sales: SnapshotSales;
  channels: SnapshotChannel[];
  period: SnapshotPeriod;
}) => {
  const t = useSnapshotTokens();
  const isMonth = period === "month";
  const supplyUnit = SUPPLY_UNIT_LABEL[period];
  const W = 1030;
  const n = Math.max(locations.length, 1);
  const H = Math.max(
    452,
    72 + (n - 1) * 110 + 68,
    salesNodeHeight(channels.length) + 60
  );
  const inbPos = { x: 66, y: H / 2 };
  const dcX = 296;
  const salesX = 838;
  const ys = locations.map((_, i) =>
    n === 1 ? H / 2 : 72 + ((H - 140) / (n - 1)) * i
  );
  const maxInbound = Math.max(1, ...locations.map((d) => d.inbound ?? 0));
  const maxOut = Math.max(1, ...locations.map((d) => d.weeklyOut ?? 0));
  const laneMidX = (inbPos.x + dcX) / 2 + 20;

  return (
    <svg
      viewBox={`0 0 ${W} ${H}`}
      style={{
        display: "block",
        width: "100%",
        maxWidth: W,
        height: "auto",
        overflow: "visible",
        fontFamily: "inherit",
      }}
    >
      {/* column captions */}
      <text
        x={dcX + 150}
        y={20}
        fontSize="9.5"
        fontWeight="700"
        letterSpacing="0.7"
        fill={t.ink4}
        textAnchor="middle"
      >
        INFLOW → STOCK
      </text>
      <text
        x={(dcX + 330 + salesX) / 2}
        y={20}
        fontSize="9.5"
        fontWeight="700"
        letterSpacing="0.7"
        fill={t.greenInk}
        textAnchor="middle"
      >
        OUTFLOW → SALES
      </text>

      {/* ── layer 1: all edges ─────────────────────────────────── */}
      {locations.map((d, i) => {
        const y = ys[i];
        return (
          <g key={d.id + "-in"}>
            <path
              d={`M${inbPos.x + 52} ${inbPos.y} C ${laneMidX} ${inbPos.y}, ${laneMidX} ${y}, ${dcX - 34} ${y}`}
              stroke={t.blue}
              strokeWidth={1.2 + ((d.inbound ?? 0) / maxInbound) * 3.4}
              fill="none"
              opacity="0.42"
              strokeLinecap="round"
            >
              <title>
                {d.inbound == null
                  ? `No inbound data for ${d.name}`
                  : `${un(d.inbound)} units inbound to ${d.name}`}
              </title>
            </path>
            <path
              d={`M${dcX - 40} ${y - 5} l7 5 -7 5z`}
              fill={t.blue}
              opacity="0.6"
            />
          </g>
        );
      })}
      {locations.map((d, i) => {
        const y = ys[i];
        const x0 = dcX + 334;
        const x1 = salesX - 6;
        return (
          <path
            key={d.id + "-outband"}
            d={`M${x0} ${y} C ${(x0 + x1) / 2} ${y}, ${(x0 + x1) / 2} ${H / 2}, ${x1} ${H / 2}`}
            stroke={t.greenInk}
            strokeWidth={d.weeklyOut ? 2 + (d.weeklyOut / maxOut) * 12 : 1}
            fill="none"
            opacity={d.weeklyOut ? 0.26 : 0.18}
            strokeLinecap="round"
          >
            <title>
              {d.weeklyOut == null
                ? `No outflow data for ${d.name}`
                : `${un(d.weeklyOut)} units/week from ${d.name} to customers`}
            </title>
          </path>
        );
      })}

      {/* ── layer 2: all edge labels, haloed ───────────────────── */}
      <g
        style={{ paintOrder: "stroke" }}
        stroke={t.surface}
        strokeWidth={3}
        strokeLinejoin="round"
      >
        {locations.map((d, i) => {
          const y = ys[i];
          const x0 = dcX + 334;
          return (
            <g key={d.id + "-lbl"}>
              <text
                x={laneMidX + 24}
                y={y - 8}
                fontSize="10.5"
                fontWeight="700"
                fill={t.blueDeep}
                textAnchor="middle"
              >
                {unOr(d.inbound)} u
              </text>
              {d.nextArrival && (
                <text
                  x={laneMidX + 24}
                  y={y + 6}
                  fontSize="9.5"
                  fill={t.ink4}
                  textAnchor="middle"
                >
                  ETA {d.nextArrival}
                </text>
              )}
              <text
                x={x0 + 26}
                y={y - 9}
                fontSize="10"
                fontWeight="700"
                fill={t.greenInk}
              >
                {d.weeklyOut == null
                  ? EM_DASH
                  : un(d.weeklyOut) + (isMonth ? "/mo" : "/wk")}
              </text>
              {d.weeklyOut != null && d.units != null && d.units > 0 && (
                <text x={x0 + 26} y={y + 5} fontSize="9" fill={t.ink4}>
                  {isMonth
                    ? `${(d.weeklyOut / d.units).toFixed(2)}× stock`
                    : `${Math.round((d.weeklyOut / d.units) * 100)}% of stock`}
                </text>
              )}
            </g>
          );
        })}
      </g>

      <SalesNode
        t={t}
        x={salesX}
        centerY={H / 2}
        sales={sales}
        channels={channels}
        period={period}
      />

      {/* inbound node */}
      <g>
        <rect
          x={inbPos.x - 52}
          y={inbPos.y - 40}
          width="104"
          height="80"
          rx="10"
          fill={t.surface3}
          stroke={t.line}
        >
          <title>Vendor POs on the water across all DCs</title>
        </rect>
        <text
          x={inbPos.x}
          y={inbPos.y - 18}
          fontSize="10"
          fill={t.ink4}
          textAnchor="middle"
        >
          Inbound POs
        </text>
        <text
          x={inbPos.x}
          y={inbPos.y + 4}
          fontSize="17"
          fontWeight="700"
          fill={t.ink}
          textAnchor="middle"
        >
          {unOr(inbound.units)}
        </text>
        <text
          x={inbPos.x}
          y={inbPos.y + 20}
          fontSize="9.5"
          fill={t.ink4}
          textAnchor="middle"
        >
          {usdOr(inbound.value)}
        </text>
        {inbound.shipments != null && (
          <text
            x={inbPos.x}
            y={inbPos.y + 34}
            fontSize="9.5"
            fill={t.ink4}
            textAnchor="middle"
          >
            {inbound.shipments} shipments
          </text>
        )}
      </g>

      {/* DC nodes — wide rects carrying metrics inline */}
      {locations.map((d, i) => {
        const y = ys[i];
        const c = healthColor(t, wosHealth(d.wos, d.targetWos));
        return (
          <g key={d.id}>
            <clipPath id={`flat-dc-clip-${i}`}>
              <rect
                x={dcX - 34}
                y={y - 38}
                width="368"
                height="76"
                rx="10"
              />
            </clipPath>
            <rect
              x={dcX - 34}
              y={y - 38}
              width="368"
              height="76"
              rx="10"
              fill={t.surface}
              stroke={c}
              strokeWidth="1.6"
            >
              <title>
                {d.units == null
                  ? `${d.name} — on-hand units unknown`
                  : `${d.name} — ${un(d.units)} units on hand`}
              </title>
            </rect>
            {/* Health bar, clipped to the node's rounded outline so it
                follows the corner radius instead of overlapping it. */}
            <rect
              x={dcX - 34}
              y={y - 38}
              width="4"
              height="76"
              fill={c}
              clipPath={`url(#flat-dc-clip-${i})`}
            />
            <text
              x={dcX - 20}
              y={y - 20}
              fontSize="12.5"
              fontWeight="700"
              fill={t.ink}
            >
              {clip(d.name, 24)}
            </text>
            {d.id !== d.name && (
              <text x={dcX + 66} y={y - 20} fontSize="9.5" fill={t.ink4}>
                {d.id}
              </text>
            )}
            <text
              x={dcX - 20}
              y={y + 4}
              fontSize="17"
              fontWeight="700"
              fill={t.ink}
            >
              {unOr(d.units)}
            </text>
            <text x={dcX - 20} y={y + 22} fontSize="9.5" fill={t.ink4}>
              {usdOr(d.value)} on hand
            </text>
            <text x={dcX + 158} y={y - 20} fontSize="9.5" fill={t.ink4}>
              {SUPPLY_ABBREV[period]}
            </text>
            <text
              x={dcX + 158}
              y={y + 4}
              fontSize="15"
              fontWeight="700"
              fill={c}
            >
              {d.displayWos == null
                ? EM_DASH
                : d.displayWos.toFixed(1) + supplyUnit}
            </text>
            <text x={dcX + 158} y={y + 22} fontSize="9.5" fill={t.ink4}>
              {d.displayTargetWos == null
                ? ""
                : `vs ${d.displayTargetWos.toFixed(1)} target`}
            </text>
            <text x={dcX + 236} y={y - 20} fontSize="9.5" fill={t.ink4}>
              In stock
            </text>
            <text
              x={dcX + 236}
              y={y + 4}
              fontSize="15"
              fontWeight="700"
              fill={
                d.inStock == null
                  ? t.ink4
                  : d.inStock < 90
                  ? t.redInk
                  : d.inStock < 94
                  ? t.orangeInk
                  : t.greenInk
              }
            >
              {d.inStock == null ? EM_DASH : d.inStock.toFixed(1) + "%"}
            </text>
            <text x={dcX + 236} y={y + 22} fontSize="9.5" fill={t.ink4}>
              {d.skus == null ? "" : `${d.skus} SKUs`}
            </text>
            <text x={dcX + 310} y={y - 20} fontSize="9.5" fill={t.ink4}>
              SKUs at risk
            </text>
            <text
              x={dcX + 310}
              y={y + 4}
              fontSize="15"
              fontWeight="700"
              fill={d.atRisk != null && d.atRisk > 15 ? t.redInk : t.ink2}
            >
              {d.atRisk == null ? EM_DASH : d.atRisk}
            </text>
          </g>
        );
      })}
    </svg>
  );
};
